import { setParamSeparator } from './format';
import { isDealerImage, hasImpolicy } from './helpers/images';

/**
 * Determines that image URL meets criteria for resize adjustments
 * Returns a boolean that checks for dealer.com image domain and no impolicy param included
 * @param {string} imageUrl - A URL string value
 */
export const isResizable = (imageUrl) =>
	isDealerImage(imageUrl) && !hasImpolicy(imageUrl);

/**
 * Determines if image URL and image location allow responsive image
 * Returns a boolean that checks `imageLocation` and if it should be resizable
 * @param {string} imageUrl - A URL string value
 * @param {string} imageLocation - A string value for defining foreground param configuraiton
 */
export const isResponsiveImageType = (imageUrl, imageLocation) => {
	return imageLocation !== 'foreground' && isResizable(imageUrl);
};

/**
 * Formats url of the imageUrl based on height and width parameters
 * Returns resized image URL
 * @param {string} imageUrl - A URL string value
 * @param {number} imageHeight - A number value for height
 * @param {number} imageWidth - A number value for width
 * @param {string} imageLocation - A string value for defining foreground param configuraiton
 */
export const setImageUrl = (
	imageUrl,
	imageHeight,
	imageWidth,
	imageLocation
) => {
	if (imageLocation === 'foreground' && isResizable(imageUrl)) {
		const resizeMethod =
			imageHeight && imageWidth ? 'downsize_crop' : 'downsize';

		// If imageHeight is not set, return early so the unveil/resizer
		// tool will take over and set the appropriate dimensions.
		if (!imageHeight) {
			return imageUrl;
		}

		// 'impolicy' parameter triggers Akamai resizing
		let resizedImage = `${imageUrl}${setParamSeparator(
			imageUrl
		)}impolicy=${resizeMethod}`;

		if (imageHeight) {
			resizedImage = `${resizedImage}&h=${imageHeight}`;
		}
		if (imageWidth) {
			resizedImage = `${resizedImage}&w=${imageWidth}`;
		}

		return resizedImage;
	} else if (imageUrl) {
		return imageUrl;
	}

	return null;
};

/**
 * default classlist added to all responsive background <img>
 */
export const defaultImgClassList = [
	'dynamic-resize',
	'responsive-image',
	'd-none'
];

/**
 * Build the loading props for a responsive image based on `isPriorityContent`
 * @param {GetImgLoadingPropsArgs} param0
 * @returns {ImageLoadingProps}
 */
export const getImgLoadingProps = ({
	isPriorityContent,
	imageUrl,
	classList
}) => {
	return {
		// If priority content, don't use data-src to lazy load content
		...{
			...(isPriorityContent ? {} : { 'data-src': imageUrl })
		},
		// If priority content, load the image instead of blank
		src: isPriorityContent ? imageUrl : '/images/blank.gif',
		// If NOT priority content, add lazy-image to classList, then join.
		className: [isPriorityContent ? '' : 'lazy-image', ...classList].join(
			' '
		)
	};
};
